import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isLoggedIn, loginEmail, startGoogleLogin, subscribe } from '../auth'
import { GOOGLE_LOGIN_ENABLED } from '../config'

/**
 * Login page — tenant-safe.
 *
 * E-mail/password against auth-api is always offered. The Google button
 * only renders when the build carries a VITE_GOOGLE_CLIENT_ID; a tenant
 * instance without its own OAuth client shows e-mail login alone instead
 * of silently sending users to the operator's Google client.
 */
export function Login() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Wohin nach dem Login: ?next=… oder Startseite. Nur relative Pfade,
  // damit ein fremdes ?next nicht auf eine andere Domain umleiten kann.
  const rawNext = params.get('next') || '/'
  const dest = rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : '/'

  // Bereits eingeloggt (oder gerade eingeloggt worden): weg von /login.
  // Vorher blieb die Seite nach erfolgreichem E-Mail-Login einfach stehen —
  // der State war gesetzt, aber niemand navigierte (Alex auf der David-
  // Instanz: „Klick auf Anmelden, sonst nichts"). Der Google-Callback hat
  // seine eigene Route und war deshalb nie betroffen.
  useEffect(() => {
    if (isLoggedIn()) navigate(dest, { replace: true })
    const unsubscribe = subscribe(() => {
      if (isLoggedIn()) navigate(dest, { replace: true })
    })
    return () => {
      unsubscribe()
    }
  }, [navigate, dest])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!email || !password) {
      setError('Bitte E-Mail und Passwort eingeben.')
      return
    }
    setBusy(true)
    try {
      await loginEmail(email.trim(), password)
      navigate(dest, { replace: true })
    } catch (err) {
      setError((err as Error).message || String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">Content</div>
        <p className="login-subtitle">Content Portal</p>

        <form onSubmit={onSubmit} className="login-form" style={{ display: 'grid', gap: 10 }}>
          <input
            type="email"
            autoComplete="username"
            placeholder="E-Mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid #cfcfcf', fontSize: 14 }}
          />
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Passwort"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid #cfcfcf', fontSize: 14 }}
          />
          {error && (
            <div role="alert" style={{ color: '#b42318', fontSize: 13 }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            style={{ padding: '10px 12px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#fff', fontSize: 14, cursor: busy ? 'default' : 'pointer' }}
          >
            {busy ? 'Anmelden …' : 'Anmelden'}
          </button>
        </form>

        {GOOGLE_LOGIN_ENABLED && (
          <>
            <div style={{ margin: '14px 0', textAlign: 'center', color: '#888', fontSize: 12 }}>oder</div>
            <button className="login-google-btn" onClick={startGoogleLogin} type="button">
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Mit Google anmelden
            </button>
          </>
        )}
      </div>
    </div>
  )
}
